import { useState, lazy, Suspense, useCallback, useEffect } from 'react';
import { Send, FileText, Loader2, RefreshCw, AlertTriangle } from 'lucide-react';
import { useWhatsappTemplates, useSaveWhatsappTemplate, useSyncWhatsappTemplates } from '@/api';
import { ErrorBoundary } from 'react-error-boundary';
import toast from 'react-hot-toast';
import type { WhatsappTemplate } from '@/types/outreach';
import styles from './WhatsappOutreach.module.css';

const WhatsappComposeTab = lazy(() =>
  import('@/components/outreach/WhatsappComposeTab').then((m) => ({
    default: m.WhatsappComposeTab,
  })),
);
const WhatsappTemplatesTab = lazy(() =>
  import('@/components/outreach/WhatsappTemplatesTab').then((m) => ({
    default: m.WhatsappTemplatesTab,
  })),
);

type Tab = 'compose' | 'templates';

export const WhatsappOutreach = () => {
  const [activeTab, setActiveTab] = useState<Tab>('compose');

  const { data: templates = [], isLoading: templatesLoading } = useWhatsappTemplates();
  const saveTemplateMutation = useSaveWhatsappTemplate();
  const syncMutation = useSyncWhatsappTemplates();

  // Auto-sync templates with Meta on mount
  useEffect(() => {
    syncMutation.mutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSaveTemplate = useCallback(
    async (template: WhatsappTemplate) => {
      try {
        await saveTemplateMutation.mutateAsync(template);
        toast.success('Template saved!');
      } catch {
        toast.error('Failed to save template');
      }
    },
    [saveTemplateMutation],
  );

  const tabs: { key: Tab; label: string; icon: typeof Send }[] = [
    { key: 'compose', label: 'Compose', icon: Send },
    { key: 'templates', label: 'Templates', icon: FileText },
  ];

  return (
    <div className={styles.container}>
      <div className={styles.inner}>
        {/* Header */}
        <div className={styles.header}>
          <div>
            <h1 className={styles.headerTitle}>WhatsApp Outreach</h1>
            <p className={styles.headerDesc}>Manage templates and send WhatsApp campaigns.</p>
          </div>
          <div className={styles.headerActions}>
            {syncMutation.isPending && (
              <span className={styles.syncIndicator}>
                <RefreshCw className={styles.syncIcon} />
                Syncing with Meta...
              </span>
            )}
            {syncMutation.isSuccess && !syncMutation.isPending && (
              <span className={styles.syncDone}>Synced</span>
            )}
          </div>
        </div>

        {/* Tab bar */}
        <div>
          <div className={styles.tabBar}>
            {tabs.map(({ key, label, icon: Icon }) => (
              <button
                key={key}
                onClick={() => setActiveTab(key)}
                className={`${styles.tabBtn} ${activeTab === key ? styles.tabBtnActive : ''}`}
              >
                <Icon className={styles.tabIcon} />
                {label}
              </button>
            ))}
          </div>

          {/* Tab content */}
          {templatesLoading ? (
            <div className={styles.loadingState}>
              <Loader2 className={styles.spinner} />
              <span className={styles.loadingText}>Loading...</span>
            </div>
          ) : (
            <ErrorBoundary
              fallback={
                <div className={styles.loadingState}>
                  <AlertTriangle size={24} />
                  <span className={styles.loadingText}>
                    Something went wrong. Please refresh the page.
                  </span>
                </div>
              }
            >
              <Suspense
                fallback={
                  <div className={styles.loadingState}>
                    <Loader2 className={styles.spinner} />
                    <span className={styles.loadingText}>Loading...</span>
                  </div>
                }
              >
                {activeTab === 'compose' && <WhatsappComposeTab templates={templates} />}
                {activeTab === 'templates' && (
                  <WhatsappTemplatesTab templates={templates} onSave={handleSaveTemplate} />
                )}
              </Suspense>
            </ErrorBoundary>
          )}
        </div>
      </div>
    </div>
  );
};
